/**
 * Shared status renderers for the Capacities masters (suppliers / models /
 * locations). Active flag → success/neutral StatusPill; location type and model
 * item-type → tinted Chip. Plain-language labels for sales/CS staff.
 */
import type { ReactNode } from 'react';
import { Chip } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import type { ItemType, LocationType } from '@/api/capacities';

/** Active/Inactive pill from the `is_active` flag. */
export function activeStatusCell(isActive?: boolean): ReactNode {
  return (
    <StatusPill tone={isActive ? 'success' : 'neutral'}>
      {isActive ? 'Active' : 'Inactive'}
    </StatusPill>
  );
}

const LOCATION_TYPE_LABEL: Record<LocationType, string> = {
  warehouse: 'Warehouse',
  datacenter: 'Data Center',
};

/** Location type ("warehouse" | "datacenter") → tinted Chip. */
export function locationTypeCell(type?: LocationType): ReactNode {
  if (!type) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
  const tone = type === 'datacenter' ? 'info' : 'neutral';
  return <Chip tone={tone}>{LOCATION_TYPE_LABEL[type] ?? type}</Chip>;
}

const ITEM_TYPE_LABEL: Record<ItemType, string> = {
  equipment: 'Equipment',
  component: 'Component',
};

/** Model item type ("equipment" | "component") → tinted Chip. */
export function itemTypeCell(type?: ItemType): ReactNode {
  if (!type) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
  const tone = type === 'equipment' ? 'accent' : 'info';
  return <Chip tone={tone}>{ITEM_TYPE_LABEL[type] ?? type}</Chip>;
}

export const locationTypeLabel = (type?: LocationType) =>
  type ? LOCATION_TYPE_LABEL[type] ?? type : '';
export const itemTypeLabel = (type?: ItemType) => (type ? ITEM_TYPE_LABEL[type] ?? type : '');
